import { Box, Paper, TextField, Typography, useTheme } from '@mui/material';
import React, { useEffect } from 'react';
import { useLoginContext } from '../../../../../contexts/LoginContext';
import { useBaseStepperContext } from '../../../shared/contexts/BaseStepperContext';
import { StepperComponentProps } from '../../types';
import { stepperStyles } from '../../../shared/styles';

const DefaultProfile: React.FC<StepperComponentProps> = ({
  description,
  label,
  stepIndex,
}) => {
  const { user, setError, selectedProfileIndex, updateProfile } =
    useLoginContext();
  const { updateStepValidation } = useBaseStepperContext();
  const selectedProfile = user?.extProfiles[selectedProfileIndex ?? 0];
  const theme = useTheme();

  // Use stepIndex prop or default to 1 for backward compatibility
  const validationStepIndex = stepIndex !== undefined ? stepIndex : 1;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name;
    const value = e.target.value;
    if (name === 'name') {
      updateProfile({ ...selectedProfile, [name]: value });
    }
  };

  const getMissingFields = React.useCallback(() => {
    const issues = [];

    if (!selectedProfile?.name) {
      issues.push('Profile name is required');
    }

    return issues;
  }, [selectedProfile?.name]);

  const missingFields = React.useMemo(
    () => getMissingFields(),
    [getMissingFields]
  );
  const hasErrors = React.useMemo(
    () => missingFields.length > 0,
    [missingFields]
  );

  const warningMessage = React.useMemo(() => {
    if (missingFields.length === 0) return '';
    if (missingFields.length === 1) return missingFields[0];
    return missingFields.join(' and ');
  }, [missingFields]);

  // Update step validation whenever validation state changes
  useEffect(() => {
    updateStepValidation(validationStepIndex, warningMessage, hasErrors);
  }, [updateStepValidation, hasErrors, warningMessage, validationStepIndex]);

  useEffect(() => {
    if (!selectedProfile || selectedProfile.name === '') {
      setError(true);
      return;
    }
    setError(false);
  }, [setError, selectedProfile, user.emailAddress]);

  return (
    <Box sx={stepperStyles.pageContainer}>
      <Box sx={stepperStyles.centeredContent}>
        <Typography
          variant="h5"
          gutterBottom
          sx={stepperStyles.pageTitle(theme)}
        >
          {label}
        </Typography>
        <Typography variant="body1" sx={stepperStyles.pageDescription(theme)}>
          {description}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            mb: 2,
          }}
        >
          Please enter a name for your profile.
        </Typography>
      </Box>

      <Paper elevation={3} sx={stepperStyles.elevatedCard(theme)}>
        <TextField
          size="small"
          variant="outlined"
          required
          fullWidth
          label="Profile Name"
          value={selectedProfile?.name || ''}
          name="name"
          onChange={handleChange}
          autoFocus
          error={!selectedProfile?.name}
          helperText={
            !selectedProfile?.name
              ? 'Please enter a profile name'
              : 'A name for this profile. This will be used to identify this profile in the future.'
          }
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default DefaultProfile;
